import { animate, style, transition, trigger } from '@angular/animations';
import { CdkTrapFocus, FocusKeyManager } from '@angular/cdk/a11y';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Directive,
	ElementRef,
	Injector,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	TemplateRef,
	computed,
	contentChild,
	inject,
	input,
	model,
	output,
	signal,
	viewChild,
	viewChildren
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Directive({
	selector: '[xpGalleryItem]',
	standalone: true
})
export class XpGalleryItemDirective {
	readonly templateRef = inject(TemplateRef);
}

@Directive({
	selector: '[xpGalleryThumbnail]',
	standalone: true
})
export class XpGalleryThumbnailDirective {
	readonly templateRef = inject(TemplateRef);
}

@Directive({
	selector: '[xpGalleryCaption]',
	standalone: true
})
export class XpGalleryCaptionDirective {
	readonly templateRef = inject(TemplateRef);
}

@Component({
	selector: 'xp-gallery',
	standalone: true,
	imports: [CommonModule, CdkTrapFocus, MatIconModule],
	templateUrl: './xp-gallery.component.html',
	styleUrls: ['./xp-gallery.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger('xpGalleryOpen', [
			transition(':enter', [
				style({ transform: 'scale(0.7)', opacity: 0 }),
				animate('150ms cubic-bezier(0, 0, 0.2, 1)')
			])
		])
	]
})
export class XpGalleryComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
	items = input.required<readonly unknown[]>();
	activeIndex = model(0);
	visibleThumbnails = input(4);
	fullScreen = input(false);
	closeLabel = input('Close');
	previousLabel = input('Previous Page');
	nextLabel = input('Next Page');
	closed = output<void>();

	protected readonly itemTemplate = contentChild(XpGalleryItemDirective);
	protected readonly thumbnailTemplate = contentChild(XpGalleryThumbnailDirective);
	protected readonly captionTemplate = contentChild(XpGalleryCaptionDirective);

	protected readonly hasActiveItem = computed(() => this.activeIndex() >= 0 && this.activeIndex() < this.items().length);
	protected readonly activeItem = computed(() => this.items()[this.activeIndex()]);
	protected readonly placeCount = computed(() => Math.max(1, Math.min(this.visibleThumbnails(), this.items().length)));
	protected readonly navDisabled = computed(() => this.items().length <= this.placeCount());
	protected readonly shift = signal(0);
	protected readonly moving = signal(false);
	protected readonly trackTransform = computed(() => `translate3d(${this.shift() * (100 / this.placeCount())}%, 0, 0)`);
	protected readonly tabbableIndex = signal(0);

	private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');
	private readonly thumbnailButtons = viewChildren<ElementRef<HTMLButtonElement>>('thumbnailButton');
	private readonly keyManager = new FocusKeyManager(
		computed(() => this.thumbnailButtons().map((button, index) => ({ focus: () => this.focusThumbnail(button.nativeElement, index) }))),
		inject(Injector)
	)
		.withHorizontalOrientation('ltr')
		.withVerticalOrientation(false)
		.withHomeAndEnd();
	private readonly document = inject(DOCUMENT);
	private readonly changeDetector = inject(ChangeDetectorRef);
	private positionedIndex = 0;
	private initialized = false;
	private savedBodyStyle?: { overflow: string; paddingRight: string };

	constructor() {
		this.keyManager.change.subscribe(index => this.tabbableIndex.set(index));
	}

	ngOnChanges(changes: SimpleChanges): void {
		const indexChanged = this.activeIndex() !== this.positionedIndex;
		if (indexChanged || changes['items'] || changes['visibleThumbnails']) {
			this.place(this.centredShift(), indexChanged && this.initialized);
		}
	}

	ngOnInit(): void {
		this.initialized = true;
		if (this.fullScreen()) {
			this.blockBodyScroll();
		}
	}

	ngAfterViewInit(): void {
		this.closeButton()?.nativeElement.focus();
	}

	ngOnDestroy(): void {
		this.keyManager.destroy();
		if (this.savedBodyStyle) {
			this.document.body.style.overflow = this.savedBodyStyle.overflow;
			this.document.body.style.paddingRight = this.savedBodyStyle.paddingRight;
		}
	}

	protected previous(): void {
		const index = this.activeIndex();
		this.activeIndex.set(index === 0 ? this.items().length - 1 : index - 1);
		this.place(this.centredShift(), true);
	}

	protected next(): void {
		const index = this.activeIndex();
		this.activeIndex.set(index === this.items().length - 1 ? 0 : index + 1);
		this.place(this.centredShift(), true);
	}

	/* The step rules of the PrimeNG Galleria thumbnail click, which differ from the centring after previous and next. */
	protected select(index: number): void {
		const active = this.activeIndex();
		if (index === active) {
			return;
		}
		const shift = this.shift();
		const median = this.medianPlace();
		let newShift = shift;
		if (index < active) {
			const dir = this.placeCount() - (index + shift) - 1 - median;
			if (dir > 0 && shift !== 0) {
				newShift = Math.min(shift + dir, 0);
			}
		} else {
			const dir = median - (index + shift);
			const pageCount = this.items().length - this.placeCount() + 1;
			if (dir < 0 && -shift < pageCount - 1) {
				newShift = Math.max(shift + dir, this.placeCount() - this.items().length);
			}
		}
		this.activeIndex.set(index);
		this.place(newShift, true);
	}

	protected isInView(index: number): boolean {
		const first = -this.shift();
		return index >= first && index < first + this.placeCount();
	}

	protected onTrackTransitionEnd(event: TransitionEvent): void {
		if (event.target === event.currentTarget) {
			this.moving.set(false);
		}
	}

	protected onThumbnailsFocusOut(event: FocusEvent): void {
		if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null) && this.isInView(this.activeIndex())) {
			this.tabbableIndex.set(this.activeIndex());
		}
	}

	protected onThumbnailKeydown(event: KeyboardEvent, index: number): void {
		switch (event.key) {
			case 'Enter':
			case ' ':
				event.preventDefault();
				this.select(index);
				break;
			case 'ArrowUp':
			case 'ArrowDown':
				event.preventDefault();
				break;
			default:
				this.keyManager.updateActiveItem(index);
				this.keyManager.onKeydown(event);
		}
	}

	private place(shift: number, animated: boolean): void {
		this.positionedIndex = this.activeIndex();
		this.tabbableIndex.set(this.positionedIndex);
		this.slide(shift, animated);
	}

	private slide(shift: number, animated: boolean): void {
		if (shift !== this.shift()) {
			this.moving.set(animated);
			this.shift.set(shift);
		}
	}

	/* A thumbnail out of view is hidden and cannot take the focus, so the row moves it into view first.
	   The focus must not scroll the viewport: it hides its overflow, and a scroll would put the row out of line with its transform. */
	private focusThumbnail(button: HTMLButtonElement, index: number): void {
		const first = -this.shift();
		const last = first + this.placeCount() - 1;
		if (index < first) {
			this.slide(-index, true);
		} else if (index > last) {
			this.slide(this.placeCount() - 1 - index, true);
		}
		this.changeDetector.detectChanges();
		button.focus({ preventScroll: true });
	}

	/* The PrimeNG Galleria rule: the active thumbnail sits in the middle place where it can, and the row stops at both ends. */
	private centredShift(): number {
		const count = this.items().length;
		const places = this.placeCount();
		const median = this.medianPlace();
		const index = this.activeIndex();
		if (index <= median) {
			return 0;
		}
		if (count - places + median < index) {
			return places - count;
		}
		if (count - places < index && places % 2 === 0) {
			return -index + median + 1;
		}
		return -index + median;
	}

	private medianPlace(): number {
		const places = this.placeCount();
		return places % 2 ? Math.floor(places / 2) : Math.floor(places / 2) - 1;
	}

	private blockBodyScroll(): void {
		const body = this.document.body;
		const scrollbarWidth = (this.document.defaultView?.innerWidth ?? 0) - this.document.documentElement.offsetWidth;
		this.savedBodyStyle = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
		body.style.overflow = 'hidden';
		body.style.paddingRight = `${scrollbarWidth}px`;
	}
}
